import CategoryCard from "../../../UI/CategoryCard/CategoryCard";
import styles from "./Categories.module.css";
import { Filter, X, RefreshCw, AlertCircle } from "lucide-react";
import { useServiceContext } from "../../../../context/ServiceContext";
import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../../../services/api";

const INITIAL_VISIBLE_COUNT = 6;
const LOAD_MORE_INCREMENT = 6;

const Categories = ({ city }) => {
  const { filterServices, searchPerformed, clearServices } =
    useServiceContext();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Fetch categories from API
  const fetchCategories = useCallback(
    async (isManualRetry = false) => {
      try {
        if (isManualRetry) {
          setIsRetrying(true);
        } else {
          setLoading(true);
        }
        setError(null);

        // If city is provided, fetch categories for that city, otherwise fetch all
        const endpoint = city
          ? `/api/categories/by-city/${city}`
          : "/api/categories";
        const response = await api.get(endpoint);

        if (response.data.success) {
          setCategories(response.data.data);
        } else {
          throw new Error(
            response.data.message || "Failed to fetch categories"
          );
        }
      } catch (err) {
        // Only log unexpected errors, not expected 404s
        if (err.response?.status !== 404) {
          console.error("Error fetching categories:", err);
        }

        const errorMessage =
          err.response?.data?.message ||
          (err.request
            ? "Secțiunea nu a fost încărcată cu succes. Te rugăm să reveniți mai târziu."
            : "A apărut o eroare neașteptată");

        setError({
          message: errorMessage,
          type: err.response
            ? "server_error"
            : err.request
            ? "connection_error"
            : "unknown",
        });
      } finally {
        setLoading(false);
        setIsRetrying(false);
      }
    },
    [city]
  );

  useEffect(() => {
    fetchCategories();
    // Reset visible count when city or filters change
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [fetchCategories, searchPerformed]);

  // Memoize filtered categories to avoid unnecessary recalculations
  const filteredCategories = useMemo(() => {
    if (!searchPerformed || filterServices.length === 0) {
      return categories;
    }

    const lowerFilterServices = filterServices.map((s) => s.toLowerCase());
    return categories.filter((category) => {
      const displayName = (
        category.displayName ||
        category.name ||
        ""
      ).toLowerCase();
      const name = (category.name || "").toLowerCase();

      return lowerFilterServices.some(
        (service) => displayName.includes(service) || name.includes(service)
      );
    });
  }, [categories, filterServices, searchPerformed]);

  // Memoize visible categories
  const visibleCategories = useMemo(
    () => filteredCategories.slice(0, visibleCount),
    [filteredCategories, visibleCount]
  );

  const hasMoreCategories = visibleCount < filteredCategories.length;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prevCount) => prevCount + LOAD_MORE_INCREMENT);
  }, []);

  const handleRetry = useCallback(() => {
    fetchCategories(true);
  }, [fetchCategories]);

  // Render error/empty state component
  const renderEmptyState = (isError = false) => (
    <div
      className={isError ? styles.errorContainer : styles.noCategoriesContainer}
    >
      <div className={isError ? styles.errorIcon : styles.noCategoriesIcon}>
        <AlertCircle size={48} />
      </div>
      <h3 className={isError ? styles.errorTitle : styles.noCategoriesTitle}>
        Nu s-au găsit servicii în orașul selectat
      </h3>
      <p className={isError ? styles.errorMessage : styles.noCategoriesMessage}>
        Încă nu oferim servicii în acest oraș. Te rugăm să selectezi un alt oraș
        sau să încerci din nou mai târziu.
      </p>
      <div
        className={isError ? styles.errorActions : styles.noCategoriesActions}
      >
        <button
          onClick={handleRetry}
          className={isError ? styles.retryButton : styles.refreshButton}
          disabled={isRetrying}
        >
          <RefreshCw size={20} className={isRetrying ? styles.spinning : ""} />
          {isRetrying ? "Se reîmprospătează..." : "Reîmprospătează"}
        </button>
      </div>
    </div>
  );

  return (
    <section
      id="servicii-populare"
      className={styles.section}
      style={{ position: "relative", zIndex: 10 }}
    >
      <div className={styles.header}>
        <div>
          <span className={styles.badge}>EXPLOREAZĂ CATEGORII</span>
          <h2 className={styles.title}>
            {searchPerformed && filterServices.length > 0
              ? "Servicii filtrate"
              : "Servicii populare"}
          </h2>
        </div>

        <div className="flex items-center">
          {searchPerformed && filterServices.length > 0 && (
            <div className={styles.filterInfo}>
              <Filter size={16} className="mr-1" />
              <span>Filtrat: {filterServices.join(", ")}</span>
              <button
                onClick={clearServices}
                className={styles.clearFilter}
                title="Șterge filtrele"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Se încarcă categoriile...</p>
        </div>
      ) : error || (categories.length === 0 && !searchPerformed) ? (
        renderEmptyState(!!error)
      ) : filteredCategories.length > 0 ? (
        <>
          <div className={styles.grid}>
            {visibleCategories.map((category) => (
              <CategoryCard
                key={
                  category._id || category.id || category.slug || category.name
                }
                category={category}
              />
            ))}
          </div>

          {/* Load More Button */}
          {hasMoreCategories && (
            <div className={styles.loadMoreContainer}>
              <button
                onClick={handleLoadMore}
                className={styles.loadMoreButton}
              >
                Încarcă mai mult
                <svg
                  className={styles.loadMoreIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles.noResults}>
          <p>Nu s-au găsit servicii care să corespundă căutării.</p>
          <button onClick={clearServices} className={styles.resetButton}>
            Resetează filtrele
          </button>
        </div>
      )}
    </section>
  );
};

export default Categories;
